/**
 * PDP bundle kit: tick/untick products, keep the running total, then check out one of two ways.
 *
 * Full kit (retail customers, endpoint set): the kit plus anything already in the cart is sent to
 * the cambie-kits draft-order API, which applies its KIT_DISCOUNT_PERCENT (15%) per kit line server-side, and the customer is
 * redirected to that checkout. The cart is cleared first, because its items are now in that order.
 *
 * Partial selection, wholesale customers or no endpoint: the regular cart add below, no discount.
 *
 * The cart add goes through the items[] form of /cart/add. That is deliberate: the Rapid Bundle app rewrites
 * a plain `quantity` add to match its own offer, but leaves items[] alone (same reason
 * pdp-revamp.js uses it for non-pack quantities).
 */
(() => {
  const formatMoney = (cents, format) => {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (precision, thousands, decimal) => {
      const parts = (cents / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return whole + (parts[1] ? decimal + parts[1] : '');
    };
    let value;
    switch ((format.match(placeholder) || [, 'amount'])[1]) {
      case 'amount_no_decimals': value = withDelimiters(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = withDelimiters(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(0, '.', ','); break;
      default: value = withDelimiters(2, ',', '.');
    }
    return format.replace(placeholder, value);
  };

  const moneyFormat = () => {
    // Reuse the format the product section already renders, so currency matches the rest of the page.
    try {
      const cfg = document.querySelector('.js-pdp-config');
      if (cfg) return JSON.parse(cfg.textContent).moneyFormat;
    } catch (e) { /* fall through */ }
    return (window.theme && window.theme.moneyFormat) || '${{amount}}';
  };

  const init = (root) => {
    const checks = [...root.querySelectorAll('.pdp-bundle__check')];
    const addBtn = root.querySelector('[data-pdp-bundle-add]');
    const countEl = root.querySelector('[data-pdp-bundle-count]');
    const totalEl = root.querySelector('[data-pdp-bundle-total]');
    const wasEl = root.querySelector('[data-pdp-bundle-was]');
    const saveEl = root.querySelector('[data-pdp-bundle-save]');
    const errorEl = root.querySelector('[data-pdp-bundle-error]');
    if (!checks.length || !addBtn) return;

    const retail = root.dataset.retail === 'true';
    const discount = Number(root.dataset.discount) || 0;
    const format = moneyFormat();
    const money = (c) => formatMoney(Math.round(c), format);

    const selected = () => checks.filter((c) => c.checked && !c.disabled);
    const selectable = () => checks.filter((c) => !c.disabled);
    // The discount only applies to the complete kit, which is what the copy promises.
    const isFullKit = () => {
      const picked = selected();
      return picked.length === selectable().length && picked.length > 1;
    };
    const endpoint = root.dataset.checkoutEndpoint;
    const usesDraftCheckout = () => retail && !!endpoint && isFullKit();
    const idleLabel = () => (isFullKit()
      ? addBtn.dataset.label
      : addBtn.dataset.labelPartial || addBtn.dataset.label) || 'Add to cart';
    let inFlight = false;

    const update = () => {
      const picked = selected();
      const full = picked.reduce((sum, c) => sum + Number(c.dataset.price || 0), 0);
      const fullKit = isFullKit();
      // Rounded per line, as the draft-order API discounts each kit line separately.
      const now = fullKit
        ? picked.reduce((sum, c) => sum + Math.round(Number(c.dataset.price || 0) * (1 - discount / 100)), 0)
        : full;

      const n = picked.length;
      // Per-card kit price, crossed-out regular price and saving, rounded per line like the total.
      const showItemSaving = retail && fullKit && discount > 0;
      checks.forEach((c) => {
        const card = c.closest('.pdp-bundle__card');
        const nowEl = card && card.querySelector('[data-pdp-bundle-item-now]');
        const itemWasEl = card && card.querySelector('[data-pdp-bundle-item-was]');
        const itemSaveEl = card && card.querySelector('[data-pdp-bundle-item-save]');
        if (!nowEl) return;
        const price = Number(c.dataset.price || 0);
        const kitPrice = showItemSaving ? Math.round(price * (1 - discount / 100)) : price;
        const itemSaving = price - kitPrice;
        nowEl.textContent = money(kitPrice);
        if (itemWasEl) {
          itemWasEl.textContent = itemSaving > 0 ? money(price) : '';
          itemWasEl.hidden = itemSaving <= 0;
        }
        if (itemSaveEl) {
          itemSaveEl.textContent = itemSaving > 0 ? `Save ${money(itemSaving)}` : '';
          itemSaveEl.hidden = itemSaving <= 0;
        }
      });

      countEl.textContent = fullKit && discount > 0
        ? `${n} items · save ${discount}% together`
        : `${n} ${n === 1 ? 'item' : 'items'}`;
      totalEl.textContent = money(now);

      if (retail) {
        const saving = full - now;
        if (wasEl) {
          wasEl.textContent = saving > 0 ? money(full) : '';
          wasEl.hidden = saving <= 0;
        }
        if (saveEl) {
          saveEl.textContent = saving > 0 ? `You save ${money(saving)} with the full kit` : '';
          saveEl.hidden = saving <= 0;
        }
      }

      if (!inFlight) {
        addBtn.disabled = picked.length === 0;
        addBtn.textContent = idleLabel();
      }
      root.classList.toggle('is-partial', !fullKit);
    };

    const showError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = !message;
    };

    const cartDrawer = () => document.querySelector('cart-drawer');

    /*
     * Opens whichever cart drawer this store actually uses and reports whether one opened.
     * Rebuy's smart cart is the visible drawer here (the theme's <cart-drawer> stays closed
     * behind it), so Rebuy is tried first, then the theme drawer, then the cart icon — clicking
     * it is what opens Rebuy for a normal add. Returns false only if none of them worked, and
     * the caller then falls back to the cart page rather than leaving the click with no result.
     */
    const openDrawer = (data) => {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data.sections && data.sections['cart-icon-bubble']) {
        bubble.innerHTML = data.sections['cart-icon-bubble'];
      }

      // Let the theme and any cart app know the cart changed before opening anything.
      document.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { cart: data, variantId: data.items && data.items[0] && data.items[0].variant_id },
      }));

      const rebuy = window.Rebuy;
      const icon = document.getElementById('cart-icon');
      if (rebuy && icon) {
        // Rebuy binds the cart icon, and clicking it opens the flyout without leaving the page.
        // Its SmartCart.show() is deliberately NOT used: on this store that navigates to /cart.
        try {
          if (rebuy.Cart && typeof rebuy.Cart.fetchCart === 'function') rebuy.Cart.fetchCart();
          icon.click();
          return true;
        } catch (e) { /* fall through to the theme drawer */ }
      }

      const drawer = cartDrawer();
      if (drawer && typeof drawer.renderContents === 'function') {
        try {
          drawer.renderContents(data, true);
          return true;
        } catch (e) { /* fall through to the cart icon */ }
      }

      if (icon) {
        icon.click();
        return true;
      }
      return false;
    };

    const add = async () => {
      const picked = selected();
      if (!picked.length) return;

      showError('');
      addBtn.disabled = true;
      addBtn.textContent = addBtn.dataset.labelAdding || 'Adding…';

      const body = new FormData();
      picked.forEach((c, i) => {
        body.append(`items[${i}][id]`, c.value);
        body.append(`items[${i}][quantity]`, 1);
      });

      // Ask for the sections the drawer needs back in the same request, as product-form.js does.
      const drawer = cartDrawer();
      const drawerSection = drawer && drawer.closest('.shopify-section');
      let sections = 'cart-icon-bubble';
      if (drawerSection) sections += `,${drawerSection.id.replace('shopify-section-', '')}`;
      body.append('sections', sections);
      body.append('sections_url', window.location.pathname);

      const routes = (window.theme && window.theme.routes) || {};

      try {
        const res = await fetch(routes.cartAdd || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add the kit');

        // Partial selections get no discount, so the code is only applied to a full kit.
        const code = isFullKit() ? addBtn.dataset.discountCode : '';
        // A discount code can only be applied by visiting /discount/CODE, so that route wins
        // over the drawer: charging the advertised price matters more than staying on the page.
        if (code || root.dataset.after === 'cart') {
          const cartUrl = routes.cart || '/cart';
          window.location.href = code
            ? `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(cartUrl)}`
            : cartUrl;
          return;
        }

        if (!openDrawer(data)) {
          window.location.href = routes.cart || '/cart';
          return;
        }
        addBtn.disabled = false;
        addBtn.textContent = idleLabel();
      } catch (err) {
        showError(err.message || 'Could not add the kit. Please try again.');
        addBtn.disabled = false;
        addBtn.textContent = idleLabel();
      }
    };

    const checkoutBundle = async () => {
      const picked = selected();
      if (!picked.length) return;

      showError('');
      addBtn.disabled = true;
      addBtn.textContent = addBtn.dataset.labelCheckout || 'Going to checkout…';

      const routes = (window.theme && window.theme.routes) || {};
      const cartUrl = routes.cart || '/cart';

      try {
        // Existing cart items go into the same order at full price. If the cart can't be read,
        // stop rather than check out without them.
        const cartRes = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (!cartRes.ok) throw new Error('Could not read your cart. Please try again.');
        const cart = await cartRes.json();
        const cartItems = (cart.items || []).map((i) => {
          const line = { variant_id: i.variant_id, quantity: i.quantity };
          if (i.properties && Object.keys(i.properties).length) line.properties = i.properties;
          return line;
        });

        const payload = {
          shop: root.dataset.shop,
          kitItems: picked.map((c) => ({ variant_id: Number(c.value), quantity: 1 })),
        };
        if (cartItems.length) payload.cartItems = cartItems;

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.url) throw new Error(data.error || 'Checkout failed. Please try again.');

        // Those items are in the draft order now, so empty the cart. A failed clear still redirects:
        // the order matters more than the leftover cart.
        if (cartItems.length) {
          await fetch(routes.cartClear ? `${routes.cartClear}.js` : '/cart/clear.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
          }).catch(() => {});
        }

        window.location.href = data.url;
        return true;
      } catch (err) {
        showError(err.message || 'Checkout failed. Please try again.');
        addBtn.disabled = false;
        addBtn.textContent = idleLabel();
        return false;
      }
    };

    const onClick = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        if (usesDraftCheckout()) {
          // The page is navigating away on success, so the button stays in its loading state.
          if (await checkoutBundle()) return;
        } else {
          await add();
        }
      } finally {
        inFlight = false;
      }
    };

    addBtn.addEventListener('click', onClick);
    update();
  };

  /*
   * Fallback kit (no bundle metafield): the page renders only the viewed product, so the other cards
   * are fetched from the recommendations endpoint, which renders this section as a bare card list.
   * Each intent is tried in order until the limit is reached, skipping variants already shown.
   * Resolves true once cards were added; with none, the section stays hidden since one item is no kit.
   */
  const loadRecommendations = async (root) => {
    const list = root.querySelector('.pdp-bundle__items');
    const limit = Number(root.dataset.recLimit) || 3;
    const intents = (root.dataset.recIntents || 'related').split(',');
    const seen = new Set([...root.querySelectorAll('.pdp-bundle__check')].map((c) => c.value));
    const cards = [];

    for (const intent of intents) {
      if (cards.length >= limit) break;
      try {
        const res = await fetch(`${root.dataset.recommendationsUrl}&intent=${intent}`);
        if (!res.ok) continue;
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await res.text();
        tmpl.content.querySelectorAll('[data-pdp-bundle-recs] .pdp-bundle__item').forEach((item) => {
          const check = item.querySelector('.pdp-bundle__check');
          if (cards.length >= limit || !check || seen.has(check.value)) return;
          seen.add(check.value);
          cards.push(item);
        });
      } catch (e) { /* try the next intent */ }
    }

    if (!list || !cards.length) return false;

    cards.forEach((item) => list.appendChild(item));
    root.querySelectorAll('[data-pdp-bundle-n]').forEach((el) => { el.textContent = list.children.length; });
    root.hidden = false;
    return true;
  };

  const setup = async (root) => {
    if (root.dataset.recommendationsUrl && !(await loadRecommendations(root))) return;
    init(root);
  };

  const initAll = () => document.querySelectorAll('[data-pdp-bundle]').forEach(setup);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-pdp-bundle]').forEach(setup));
})();
